import { useState, useEffect } from 'react';
import { Trophy, Medal, ThumbsUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

export default function Leaderboard() {
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get('/servers/leaderboard', { params: { limit: 50 } });
      setServers(response.data.servers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLoading(false);
    }
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-yellow-400" />;
    if (index === 1) return <Medal className="w-6 h-6 text-gray-300" />;
    if (index === 2) return <Medal className="w-6 h-6 text-orange-400" />;
    return <span className="text-xl font-bold text-muted-foreground">#{index + 1}</span>;
  };

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E055]/10 border border-[#00E055]/20 rounded-sm text-[#00E055] text-xs font-bold tracking-widest uppercase mb-6">
              <Trophy className="w-4 h-4" />
              TOP SERVERS
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="leaderboard-title">
              LEADER<span className="text-[#00E055]">BOARD</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-4">
              The most voted servers in MalluRealm
            </p>
          </div>

          {/* Leaderboard */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-3" data-testid="leaderboard-list">
              {servers.map((server, index) => (
                <div
                  key={server.id}
                  className={`bg-[#0A0A0A] border rounded-lg p-6 cursor-pointer hover:border-[#00E055]/50 transition-all ${
                    index < 3 ? 'border-[#00E055]/30' : 'border-border'
                  }`}
                  onClick={() => navigate(`/servers/${server.slug || server.id}`)}
                  data-testid={`leaderboard-rank-${index + 1}`}
                >
                  <div className="flex items-center gap-6">
                    {/* Rank */}
                    <div className="flex items-center justify-center w-12">
                      {getRankIcon(index)}
                    </div>

                    {/* Logo */}
                    {server.logo && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-[#00E055]/20">
                        <img src={server.logo} alt={server.name} className="w-full h-full object-cover" />
                      </div>
                    )}

                    {/* Server Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold tracking-tight mb-1" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid={`server-name-${index + 1}`}>
                        {server.name}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        <span 
                          className="px-3 py-1 text-[10px] font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
                          style={{ fontFamily: 'Unbounded, sans-serif' }}
                        >
                          <span className="w-1 h-1 rounded-full bg-[#00E055] shadow-[0_0_5px_#00E055]" />
                          {server.category}
                        </span>
                        <span 
                          className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 rounded-sm"
                          style={{ fontFamily: 'Unbounded, sans-serif' }}
                        >
                          {server.version}
                        </span>
                      </div>
                    </div>

                    {/* Votes */}
                    <div className="text-right">
                      <div className="flex items-center gap-2 text-[#00E055]" data-testid={`server-votes-${index + 1}`}>
                        <ThumbsUp className="w-5 h-5" />
                        <span className="text-2xl font-black">{server.vote_count}</span>
                      </div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">VOTES</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
